// @ts-check

import { buildCsv } from './csv';

const HEADER = ['Product', 'Size', 'Color', 'SKU', 'MRP', 'MRP Text', 'Composition', 'Care Instructions', 'QR URL'];

// The storefront's middleware redirects a bare /product/... path to a country-prefixed one (see
// frontend-next/middleware.ts, falling back to this same default when it can't detect a country),
// but a printed tag can't depend on that redirect hop working for every scanner/network - bake in
// a country directly. Default matches frontend-next's own default (US) for when the export runs
// before a country has been picked (the on-screen preview) or with old-shaped, pre-picker data.
const DEFAULT_COUNTRY_SLUG = 'us';

/**
 * CSV for the hanging-tag printer: one row per tag copy, from `/api/admin/inventory/all-variants`
 * rows plus a `copies` count. `issues` lists rows that would print with a blank field.
 *
 * MRP is what checkout charges. The "MRP Text" column never includes the word "MRP" itself - that
 * label is added at tag-creation time (design/print step), not baked into the data. When the row
 * carries a converted price (formatted_price, from picking a country in the export bar - see
 * utils/price_display on the backend), that converted, pre-formatted value is used as-is, with no
 * added tax phrase (a US/EU duty-inclusive price isn't "inclusive of taxes" the way Indian GST
 * is). Otherwise falls back to the raw INR price with the existing GST wording, unchanged.
 * @param {Array<{product_name: string, product_slug?: string, size?: string | null, color?: string | null, sku?: string | null, price: number, sale_price?: number | null, display_price?: number, display_sale_price?: number | null, formatted_price?: string, formatted_sale_price?: string | null, copies: number, composition_shell_name?: string | null, composition_lining_name?: string | null, care_instructions?: string | null}>} variants
 * @param {string} siteUrl storefront origin, e.g. https://kaay.co
 * @param {string} [countrySlug] storefront URL country prefix, e.g. 'us', 'uk' - defaults to 'us'
 * @returns {{csv: string, count: number, issues: Array<{product_name: string, size?: string | null, problem: string}>}}
 */

/** Shell/lining are per-product (see backend's _variant_to_dict), already resolved to a name. */
function compositionText(v) {
  const shell = v.composition_shell_name;
  const lining = v.composition_lining_name;
  if (shell && lining) return `Shell: ${shell}; Lining: ${lining}`;
  if (lining) return `Lining: ${lining}`;
  return shell || '';
}

export function buildTagCsv(variants, siteUrl, countrySlug = DEFAULT_COUNTRY_SLUG) {
  const origin = siteUrl.replace(/\/+$/, '');
  /** @type {Array<Array<string | number | null | undefined>>} */
  const rows = [HEADER];
  const issues = [];
  let count = 0;

  for (const v of variants) {
    if (!(v.copies > 0)) continue;

    const isConverted = v.formatted_price != null;
    const mrp = isConverted
      ? Number(v.display_sale_price ?? v.display_price) || 0
      : Math.round(Number(v.sale_price || v.price) || 0);
    const qrUrl = v.product_slug ? `${origin}/${countrySlug}/product/${v.product_slug}` : '';
    const mrpText = mrp <= 0
      ? ''
      : isConverted
        ? `${v.formatted_sale_price || v.formatted_price}`
        : `₹${mrp.toLocaleString('en-IN')} inclusive of all taxes`;

    if (!v.sku) issues.push({ product_name: v.product_name, size: v.size, problem: 'Missing SKU' });
    if (mrp <= 0) issues.push({ product_name: v.product_name, size: v.size, problem: 'Missing price' });
    if (!qrUrl) issues.push({ product_name: v.product_name, size: v.size, problem: 'Missing product link' });

    for (let i = 0; i < v.copies; i++) {
      rows.push([v.product_name, v.size, v.color, v.sku, mrp > 0 ? mrp : '', mrpText, compositionText(v), v.care_instructions, qrUrl]);
      count++;
    }
  }

  return { csv: buildCsv(rows), count, issues };
}
